/**
 * Campaign Rebuild Page
 * 
 * Re-enters the group-based campaign builder with existing data prefilled.
 * Universal rebuild: available for ALL campaign types in draft status.
 * 
 * Data sources (priority order):
 * 1. groups_json from manual_campaigns (if exists)
 * 2. Convert from campaign_snapshots.packs_snapshot
 *    - Pack campaigns: preserves pack structure as separate groups
 *    - Manual/pack_manual: merges into single converted group
 * 
 * Eligibility:
 * - Must be draft status
 * - Must NOT have locked assignments in the cycle
 * - Must have convertible data (snapshot or groups)
 * 
 * Flow:
 * 1. Load existing groups from campaign (or convert from snapshot)
 * 2. Display RestructureGroupBuilder with prefilled data
 * 3. User modifies structure as needed
 * 4. Execution Preview (mandatory)
 * 5. Save creates new snapshot revision
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiJson, apiFetch } from '@/lib/api';
import { getCampaignIntegrityUserMessage } from '@/lib/campaignIntegrityErrors';
import {
  appendListReturnParams,
  buildCampaignsListUrlFromReturnParams,
} from '@/lib/campaignsListUrl';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    ChevronLeft,
    AlertTriangle,
    Layers,
    ArrowRight,
    Loader2,
    CheckCircle2,
    Info
} from 'lucide-react';
import { RestructureGroupBuilder } from '@/components/campaigns/RestructureGroupBuilder';
import { StoreFormatGroupEditor } from '@/components/campaigns/StoreFormatGroupEditor';
import { ExecutionPreview } from '@/components/campaigns/ExecutionPreview';
import type { GroupValidationError, GroupValidationWarning, StoreFormatGroup } from '@shared/store-format-groups';
import { campaignRebuildKeys } from '@/queries/campaignRebuildKeys';

type RebuildStep = 'editor' | 'preview';

interface RebuildDataResponse {
    groups?: StoreFormatGroup[] | null; // Optional - may be missing or null
    isConverted: boolean;
    conversionNotes: string[];
    campaignType: string;
    isEligible: boolean;
    eligibilityReason?: string;
}

interface CampaignSummary {
    id: number;
    name: string;
    retailerId?: number | null;
    cycleId?: number | null;
}

export default function CampaignRebuild() {
    const { id } = useParams();
    const campaignId = parseInt(id || '0');
    const [, navigate] = useLocation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const listReturnSearch = useMemo(() => window.location.search, []);
    const campaignsListBackUrl = useMemo(
      () => buildCampaignsListUrlFromReturnParams(listReturnSearch),
      [listReturnSearch],
    );
    const campaignDetailBackUrl = useMemo(
      () => appendListReturnParams(`/campaigns/${campaignId}`, listReturnSearch),
      [campaignId, listReturnSearch],
    );

    // State
    const [currentStep, setCurrentStep] = useState<RebuildStep>('editor');
    const [groups, setGroups] = useState<StoreFormatGroup[]>([]);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [isValidatingForPreview, setIsValidatingForPreview] = useState(false);
    const [validationFeedback, setValidationFeedback] = useState<{
        errors: GroupValidationError[];
        warnings: GroupValidationWarning[];
    }>({ errors: [], warnings: [] });

    // Fetch campaign data
    const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery<CampaignSummary | null>({
        queryKey: ['/api/campaigns', campaignId],
        queryFn: async () => {
            try {
                const response = await apiJson<CampaignSummary>(`/api/campaigns/${campaignId}`);
                return response;
            } catch (error: any) {
                if (error.status === 404) return null;
                throw error;
            }
        },
        enabled: !!campaignId,
    });

    // Fetch rebuild data (handles both existing groups and legacy conversion)
    const { data: rebuildData, isLoading: rebuildDataLoading, error: rebuildDataError } = useQuery<RebuildDataResponse>({
        queryKey: campaignRebuildKeys.rebuildData(campaignId),
        queryFn: async () => {
            const response = await apiJson<RebuildDataResponse>(`/api/campaigns/${campaignId}/rebuild-data`);
            return response;
        },
        enabled: !!campaignId,
        staleTime: 0, // Always refetch to get latest groups data
    });

    // Reset initialization when campaignId changes
    React.useEffect(() => {
        setGroups([]);
        setHasUserEdited(false);
    }, [campaignId]);

    // Initialize groups from rebuild data
    React.useEffect(() => {
        if (rebuildData && rebuildData.isEligible && !hasUserEdited) {
            // Ensure groups is always an array - defensive check
            let groupsArray: StoreFormatGroup[] = [];
            if (rebuildData.groups) {
                if (Array.isArray(rebuildData.groups)) {
                    groupsArray = rebuildData.groups;
                } else {
                    console.error('[CampaignRebuild] rebuildData.groups is not an array:', typeof rebuildData.groups, rebuildData.groups);
                    groupsArray = [];
                }
            }
            setGroups(groupsArray);
        }
    }, [rebuildData, hasUserEdited, campaignId]);

    // Get retailer/cycle info for editor
    const retailerId = (campaign as any)?.retailerId ?? 0;
    const cycleId = (campaign as any)?.cycleId ?? null;

    // Handle groups change
    const handleGroupsChange = useCallback((newGroups: StoreFormatGroup[]) => {
        if (!hasUserEdited) {
            setHasUserEdited(true);
        }
        setValidationFeedback({ errors: [], warnings: [] });
        setGroups(newGroups);
    }, [hasUserEdited]);

    // Continue to preview
    const handleContinueToPreview = useCallback(async () => {
        if (groups.length === 0) {
            toast({
                title: 'No Groups',
                description: 'Please add at least one group before continuing.',
                variant: 'destructive',
            });
            return;
        }

        setIsValidatingForPreview(true);
        try {
            const response = await apiFetch(`/api/campaigns/${campaignId}/groups/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groups }),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Validation failed');
            }

            const validation = await response.json();
            const validationErrors = Array.isArray(validation?.errors) ? validation.errors : [];
            const validationWarnings = Array.isArray(validation?.warnings) ? validation.warnings : [];
            setValidationFeedback({ errors: validationErrors, warnings: validationWarnings });
            if (validationErrors.length > 0) {
                toast({
                    title: 'Fix group issues first',
                    description: validationErrors[0]?.message || 'The campaign structure is not valid yet.',
                    variant: 'destructive',
                });
                return;
            }
        } catch (error: any) {
            toast({
                title: 'Validation failed',
                description: error?.message || 'Could not validate the campaign structure.',
                variant: 'destructive',
            });
            return;
        } finally {
            setIsValidatingForPreview(false);
        }

        setCurrentStep('preview');
    }, [campaignId, groups, toast]);

    // Go back to editor
    const handleBackToEditor = useCallback(() => {
        setCurrentStep('editor');
    }, []);

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async () => {
            const response = await apiFetch(`/api/campaigns/${campaignId}/rebuild`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groups }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to save rebuilt campaign');
            }
            return response.json();
        },
        onSuccess: (data) => {
            toast({
                title: 'Campaign Rebuilt',
                description: 'Campaign structure has been updated successfully.',
            });
            // Invalidate all campaign tab queries - Groups, Sitelist, Assignments, Header
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId, 'groups-view'] });
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId, 'summary'] });
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId, 'assignments'] });
            queryClient.invalidateQueries({ queryKey: ['campaign-favourite-images', campaignId] });
            // Invalidate snapshot queries - rebuild now regenerates the snapshot
            queryClient.invalidateQueries({ queryKey: ['campaign-snapshot'] });
            queryClient.invalidateQueries({ queryKey: ['/api/campaign-snapshots'] });
            queryClient.invalidateQueries({ queryKey: ['cycle-snapshots', cycleId] });
            // Invalidate rebuild-data so it fetches fresh on next rebuild
            queryClient.invalidateQueries({ queryKey: campaignRebuildKeys.rebuildData(campaignId) });
            // Navigate back to campaign details
            navigate(campaignDetailBackUrl);
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: getCampaignIntegrityUserMessage(error, 'rebuild'),
                variant: 'destructive',
            });
        },
    });

    // Handle save
    const handleSave = useCallback(() => {
        saveMutation.mutate();
    }, [saveMutation]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        navigate(campaignDetailBackUrl);
    }, [navigate, campaignId]);

    // Loading state
    if (campaignLoading || rebuildDataLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/30">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">Loading campaign data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (campaignError || rebuildDataError) {
        return (
            <div className="p-6 bg-muted/30 min-h-screen">
                <Card>
                    <CardContent className="py-8 text-center">
                        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-red-900 mb-2">Error Loading Campaign</h2>
                        <p className="text-muted-foreground mb-4">
                            {(campaignError || rebuildDataError) instanceof Error
                                ? (campaignError || rebuildDataError as Error).message
                                : 'Failed to load campaign data'}
                        </p>
                        <Button variant="outline" onClick={() => navigate(campaignDetailBackUrl)}>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Campaign
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Not found
    if (!campaign) {
        return (
            <div className="p-6 bg-muted/30 min-h-screen">
                <Card>
                    <CardContent className="py-8 text-center">
                        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">Campaign Not Found</h2>
                        <p className="text-muted-foreground mb-4">
                            The requested campaign could not be found.
                        </p>
                        <Button variant="outline" onClick={() => navigate(campaignsListBackUrl)}>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Campaigns
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Not eligible (based on rebuildData response)
    if (!rebuildData?.isEligible) {
        return (
            <div className="p-6 bg-muted/30 min-h-screen">
                <Card>
                    <CardContent className="py-8 text-center">
                        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">Campaign Cannot Be Rebuilt</h2>
                        <p className="text-muted-foreground mb-4">
                            {rebuildData?.eligibilityReason || 'This campaign is not eligible for rebuild.'}
                        </p>
                        <Button variant="outline" onClick={() => navigate(campaignDetailBackUrl)}>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Campaign
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 bg-muted/30 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={handleCancel}>
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Cancel
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Layers className="h-6 w-6" />
                            Restructure Campaign
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {(campaign as any)?.name || 'Campaign'}
                        </p>
                    </div>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                    Restructure Mode
                </Badge>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 text-sm">
                <div className={`flex items-center gap-1 ${currentStep === 'editor' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'editor' ? 'bg-primary text-white' : 'bg-muted'}`}>
                        1
                    </div>
                    Edit Structure
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className={`flex items-center gap-1 ${currentStep === 'preview' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'preview' ? 'bg-primary text-white' : 'bg-muted'}`}>
                        2
                    </div>
                    Preview & Confirm
                </div>
            </div>

            {/* Info alert */}
            <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                    <strong>Restructure Mode:</strong> Changes won't be saved until you confirm in the preview step.
                    Cancel at any time to discard changes.
                </AlertDescription>
            </Alert>

            {/* Conversion notice for legacy campaigns */}
            {rebuildData?.isConverted && (
                <Alert className="bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                        <strong>Converted from legacy format:</strong> This campaign was automatically converted
                        from the legacy manual format into a single group for editing.
                        {rebuildData.conversionNotes && rebuildData.conversionNotes.length > 0 && (
                            <ul className="mt-2 list-disc list-inside text-sm">
                                    {rebuildData.conversionNotes.map((note: string, idx: number) => (
                                    <li key={idx}>{note}</li>
                                ))}
                            </ul>
                        )}
                    </AlertDescription>
                </Alert>
            )}

            {/* Content based on step */}
            {currentStep === 'editor' ? (
                <div className="space-y-6">
                    <RestructureGroupBuilder
                        campaignId={campaignId}
                        retailerId={retailerId}
                        groups={groups}
                        onGroupsChange={handleGroupsChange}
                        validationErrors={validationFeedback.errors}
                        validationWarnings={validationFeedback.warnings}
                    />

                    <details className="rounded-xl border bg-white shadow-sm">
                        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
                            Fine-tune group details: stores, formats, quantities, labels, POPAd coverage, and instructions
                        </summary>
                        <div className="space-y-3 border-t p-4">
                            <p className="text-sm text-muted-foreground">
                                Use this panel for detailed adjustments that are too granular for the main Restructure layout. Changes here update the same groups and are included in validation, preview, and save.
                            </p>
                            <StoreFormatGroupEditor
                                campaignId={campaignId}
                                retailerId={retailerId}
                                cycleId={cycleId}
                                groups={groups}
                                onGroupsChange={handleGroupsChange}
                                readOnly={false}
                            />
                        </div>
                    </details>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button onClick={handleContinueToPreview} disabled={groups.length === 0 || isValidatingForPreview}>
                            {isValidatingForPreview ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Validating...
                                </>
                            ) : (
                                <>
                                    Continue to Preview
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <ExecutionPreview
                        groups={groups}
                        onBack={handleBackToEditor}
                        onContinue={handleSave}
                        isSubmitting={saveMutation.isPending}
                    />

                    {/* Restructure-specific save actions - override ExecutionPreview's Continue button */}
                    <div className="flex items-center justify-between pt-4 border-t">
                        <Button variant="outline" onClick={handleBackToEditor} disabled={saveMutation.isPending}>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Editor
                        </Button>
                        <Button onClick={handleSave} disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Save Restructured Campaign
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
